<template>
  <div :class="componentClassName">
    <div class="o-global-sidebar__scroll">
      <div class="o-global-sidebar__logo">
        <a-brand-logo variant="horizontal" />
      </div>
      <div class="o-global-sidebar__user-status">
        <m-current-user-status
          v-if="currentUserInfo"
          :online="currentUserInfo.online"
          :business-hour="currentUserInfo.business_hour"
          :user-name="currentUserInfo.name"
          :company-id="currentUserInfo.company_id"
          :edit-profile-url="currentUserInfo.edit_profile_url"
          :logout-url="currentUserInfo.logout_url"
          :phone-number="currentUserInfo.phone_number"
          :auth-info="currentUserInfo.auth_info"
          :handle-online-status-change="handleOnlineStatusChange"
          :handle-logout="handleLogout"
        />
      </div>
      <div class="o-global-sidebar__navigation">
        <m-navigation>
          <template v-for="item in navigationItems" :key="item.nav_id">
            <a-navigation-item
              :nav-id="item.nav_id"
              :active="isNavActive(item)"
              :icon="item.icon"
              :active-icon="item.active_icon"
              :icon-size="item.icon_size"
              :label="item.label"
              :has-submenu="item.has_submenu"
              :expand="item.has_submenu && isSubNavActive(item)"
              @click="handleClick(item.route_name, item.company_id_selector)"
            >
              <a-navigation-item
                v-for="subItem in item.items"
                :key="subItem.nav_id"
                :nav-id="subItem.nav_id"
                :active="isNavActive(subItem)"
                :icon="subItem.icon"
                :label="subItem.label"
                @click="handleClick(subItem.route_name, subItem.company_id_selector)"
              />
            </a-navigation-item>
          </template>
        </m-navigation>
      </div>
      <div v-if="!canMigrateFromTrial" class="o-global-sidebar__service">
        <div class="o-global-sidebar__service-end">
          <div class="o-global-sidebar__service-text-note">
            COTOHA Call Centerのご提供は、<br />
            2025年3月31日をもちまして終了<br />します。<br />
            サービス終了に関するおしらせは<br />
            <a :href="serviceEndUrl" target="blank">こちら</a>
          </div>
        </div>
      </div>
      <div v-if="canMigrateFromTrial" class="o-global-sidebar__trial">
        <div v-if="trialStatus === 'use'" class="o-global-sidebar__trial-use">
          <div v-if="trialEndPeriod > 0" class="o-global-sidebar__trial-textbox">
            <div class="o-global-sidebar__trial-text">無料トライアル終了まで</div>
            <br />
            <div class="o-global-sidebar__trial-text">
              あと
              <div class="o-global-sidebar__trial-text-period">
                {{ trialEndPeriod }}
              </div>
              日
            </div>
          </div>
          <div v-if="trialEndPeriod <= 0" class="o-global-sidebar__trial-textbox">
            <div class="o-global-sidebar__trial-text">無料トライアルは</div>
            <br />
            <div class="o-global-sidebar__trial-text-period">本日終了</div>
          </div>
          <div class="o-global-sidebar__trial-text-note">
            終了後も7日間はログインできます。<br />(内線通話のみご利用いただけません)
          </div>
          <a-button
            id="invite"
            class="o-global-sidebar__migratebutton"
            variant="round"
            color="primary"
            @click="handleInviteLink()"
            >本申し込みに進む</a-button
          >
        </div>
        <div v-if="trialStatus === 'transiting'" class="o-global-sidebar__trial-transiting">
          <div class="o-global-sidebar__trial-textbox">
            <div class="o-global-sidebar__trial-text">無料トライアルは</div>
            <br />
            <div class="o-global-sidebar__trial-text">終了しました。</div>
          </div>
          <a-button
            id="invite"
            class="o-global-sidebar__migratebutton"
            variant="round"
            color="primary"
            @click="handleInviteLink()"
            >本申し込みに進む</a-button
          >
        </div>
      </div>
    </div>
    <teleport to="body">
      <o-modal
        id="global_resultModal"
        v-model:visible="resultModalVisible"
        class="o-modal-remove-phone-number-confirmation"
        footer-variant="confirmation"
        width="442px"
        :footer-shadow="false"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
      >
        <template #title>{{ resultModalTitle }}</template>
        <a-text>
          <!-- eslint-disable -->
          <span v-html="resultModalContent" />
          <!-- eslint-enable -->
        </a-text>
        <template #footer>
          <a-button
            id="confirmResult"
            variant="block"
            color="primary"
            label="OK"
            size="small"
            @click="handleCloseModal"
          />
        </template>
      </o-modal>
    </teleport>
  </div>
</template>

<script>
import { mapModifiers } from '@/libs/component';
import { CCC_SERVICE_END_URL } from '@/libs/constants';
import ABrandLogo from '@/components/atoms/brand-logo';
import ANavigationItem from '@/components/atoms/navigation-item';
import MCurrentUserStatus from '@/components/molecules/current-user-status';
import MNavigation from '@/components/molecules/navigation';
import Simplebar from 'simplebar';
import dayjs from 'dayjs';
import { mapActions } from 'vuex';
import { customRequest } from '@/common/customRequest.js';
import { messageContent } from '@/common/messageContent.js';
import { POINT, CCC_TRIAL_PERIOD, DEFAULT_TIMEZONE } from '@/libs/constants';

dayjs.extend(require('dayjs/plugin/timezone'));
dayjs.extend(require('dayjs/plugin/utc'));
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

export default {
  name: 'o-global-sidebar',
  components: {
    ABrandLogo,
    ANavigationItem,
    MCurrentUserStatus,
    MNavigation,
  },
  mixins: [customRequest],
  props: {
    variant: {
      type: String,
      default: null,
      validator(value) {
        return value.match(/(null|primary)/);
      },
    },
    class: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    currentUserInfo: {
      type: Object,
      default: null,
    },
    navigationItems: {
      type: Array,
      default: new Array(0),
    },
    handleOnlineStatusChange: {
      type: Function,
      default: () => {},
    },
    handleLogout: {
      type: Function,
      default: () => {},
    },
    username: {
      type: String,
      default: '',
    },
    companyID: {
      type: String,
      default: '',
    },
    serviceEndUrl: {
      type: String,
      default: CCC_SERVICE_END_URL,
    },
  },
  data() {
    return {
      isAuthenticated: true,
      resultModalTitle: '',
      resultModalContent: '',
      resultModalVisible: false,
      trialStatus: '',
      trialEndPeriod: NaN,
      currentActiveItemId: 'home',
    };
  },
  computed: {
    componentClassName() {
      const baseClassName = mapModifiers('o-global-sidebar', this.variant);
      return `${baseClassName} ${this.class}`.trim();
    },
    canMigrateFromTrial() {
      const companyID = this.currentUserInfo.company_id;
      const authInfo = this.currentUserInfo.auth_info;
      return /^tri\d{6}$/.exec(companyID) && authInfo === 'supervisor' ? true : false;
    },
    isPOINT() {
      return this.currentUserInfo.company_id === POINT;
    },
    screen() {
      return this.$store.state.screen;
    },
  },
  watch: {
    screen(val) {
      this.isAuthenticated = val && val.status === 'transitioned';
      if (this.isAuthenticated) {
        this.updateActiveNavId();
      }
    },
  },
  created() {
    this.updateActiveNavId();
  },
  mounted() {
    new Simplebar(this.$el.querySelector('.o-global-sidebar__scroll'));
    this.getTrialInfo();
  },
  methods: {
    ...mapActions(['updateScreen']),
    updateActiveNavId() {
      if (!this.screen || !this.screen.currentScreenName) return;
      for (const navItem of this.navigationItems) {
        if (navItem.route_name === this.screen.currentScreenName) {
          this.currentActiveItemId = navItem.nav_id;
          return;
        }
        if (!navItem.items) continue;
        for (const item of navItem.items) {
          if (item.route_name === this.screen.currentScreenName) {
            this.currentActiveItemId = item.nav_id;
            return;
          }
        }
      }
    },
    isNavActive(navItem) {
      let active = false;
      if (navItem.nav_id === this.currentActiveItemId) {
        active = true;
      } else {
        // check child active
        active = this.isSubNavActive(navItem);
      }
      return active;
    },
    isSubNavActive(navItem) {
      let active = false;
      if (navItem.items) {
        navItem.items.forEach(subNavItem => {
          if (subNavItem.nav_id === this.currentActiveItemId) {
            active = true;
          }
        });
      }
      return active;
    },
    // トライアル企業の情報取得
    async getTrialInfo() {
      // トライアル企業でない場合は何もしない
      if (!this.canMigrateFromTrial) return;
      // trialStatusとstartDateからの日数を算出
      try {
        const ret = await this.postRequest('/contract/getcontractdata', { companyID: this.currentUserInfo.company_id });
        const companyInfo = ret.data;
        // trialStatusの取得
        this.trialStatus = companyInfo.trialStatus;
        // トライアル期間の算出・取得
        if (this.trialStatus === 'use') {
          const registDate = dayjs().tz();
          const startDate = companyInfo.startDate;
          const limitDayTime = dayjs(startDate).tz().add(CCC_TRIAL_PERIOD, 'day');
          this.trialEndPeriod = -registDate.diff(limitDayTime, 'day');
        }
      } catch (error) {
        console.log(error);
      }
    },
    handleClick: function (route_name, company_id_selector) {
      if (!route_name || route_name === this.$route.name) return;
      if (this.isAuthenticated) {
        this.isAuthenticated = false;
        if (company_id_selector && this.isPOINT) {
          this.$router.push({ name: route_name, params: { isAuthenticated: false }, query: { companyID: POINT } });
        } else {
          this.$router.push({ name: route_name, params: { isAuthenticated: false } });
        }
        this.updateScreen({ currentScreenName: this.$route.name, status: 'transitioning' });
      } else {
        console.warn('The menu is temporarily disabled due to waiting for authentication!');
      }
    },
    handleInviteLink: function () {
      // トライアル移行情報取得
      this.postRequest('/contract/getcontractdata', { companyID: this.currentUserInfo.company_id })
        .then(e => {
          if (typeof e.data.contractedCompanyID !== 'undefined' && e.data.contractedCompanyID !== '') {
            this.showErrorModal(messageContent.trialCompanyIsContracted);
          } else {
            // 申し込み画面を別タブで開く
            const resolvedRoute = this.$router.resolve({ name: 'Signup' });
            const invitationWindow = window.open(resolvedRoute.href, '_blank');
            // 申し込み画面のロード(created)が完了し次第、messageイベント通知でトライアル移行フラグを送信
            invitationWindow.onload = () => {
              invitationWindow.postMessage('trialMigration', invitationWindow.origin);
            };
          }
        })
        .catch(error => {
          console.log(error);
        });
    },
    handleCloseModal() {
      this.resultModalVisible = false;
      this.resultModalTitle = '';
      this.resultModalContent = '';
    },
    showErrorModal(message) {
      this.resultModalVisible = true;
      this.resultModalTitle = messageContent.errorModalTitle;
      this.resultModalContent = message;
    },
  },
};
</script>
